import React, { useEffect, useState } from 'react';
import { Button, Col, Form, Input, Row, Select, notification } from 'antd';

import type { BusPassReportFormValues } from '../../../types';
import { generateBusPass } from '../../../service/httpsCalls';

const { Option } = Select;
const { TextArea } = Input;

const BUS_PASS_TYPE_OPTIONS = [
    'Bus Pass Daily - Electronic',
    'Bus Pass Daily - Physical',
    'Bus Pass Monthly - Electronic',
    'Bus Pass Monthly - Physical',
];

const ReadOnlyField = ({ label, value }: { label: string; value?: React.ReactNode }) => (
    <div style={{ marginBottom: 24 }}>
        <div style={{ color: 'rgba(0, 0, 0, 0.45)', fontSize: 12, marginBottom: 4 }}>
            {label}
        </div>
        <div style={{ minHeight: 22, overflowWrap: 'anywhere' }}>
            {value || '-'}
        </div>
    </div>
);

interface BusPassReportFormProps {
    initialValues?: Partial<BusPassReportFormValues>;
    onReportGenerated?: (data: any) => void;
}

const BusPassReportForm: React.FC<BusPassReportFormProps> = ({
    initialValues,
    onReportGenerated,
}) => {
    const [form] = Form.useForm<BusPassReportFormValues>();
    const [submitting, setSubmitting] = useState(false);
    const [readOnlyValues, setReadOnlyValues] = useState<Partial<BusPassReportFormValues>>({});
    const [memberDisplay, setMemberDisplay] = useState('');
    const [memberNavigationId, setMemberNavigationId] = useState('');
    const [tripLegsDisplay, setTripLegsDisplay] = useState('');
    const [preferredPassTagDisplay, setPreferredPassTagDisplay] = useState('');
    const [vendorDisplay, setVendorDisplay] = useState('');
    const [notificationApi, notificationContextHolder] = notification.useNotification();

    const getMemberDetailUrl = (memberId?: string) => {
        if (!memberId) {
            return '';
        }

        const baseUrl = window.localStorage.getItem('Referrer') ?? '';
        return `${baseUrl}/Member/Member/Detail?prm=${btoa(memberId)}`;
    };

    useEffect(() => {
        if (!initialValues || !Object.keys(initialValues).length) {
            form.resetFields();
            setReadOnlyValues({});
            setMemberDisplay('');
            setMemberNavigationId('');
            setTripLegsDisplay('');
            setPreferredPassTagDisplay('');
            setVendorDisplay('');
            return;
        }

        const pickValue = (...keys: string[]): string => {
            for (const key of keys) {
                const raw = (initialValues as Record<string, any>)[key];
                if (raw !== undefined && raw !== null && raw !== '') {
                    if (typeof raw === 'object') {
                        return '';
                    }
                    return String(raw);
                }
            }
            return '';
        };

        const storedUserName = (() => {
            try {
                return JSON.parse(window.localStorage.getItem('userData') ?? '{}')?.userName ?? '';
            } catch {
                return '';
            }
        })();

        const memberId = pickValue('MbrID', 'memberId');
        const memberNavigationIdValue = pickValue('MemberId', 'MemberID', 'memberNavigationId') || memberId;
        const memberName = pickValue('MbrName', 'memberName');
        const subcontractorId = pickValue('SubcontractorIDToAssign', 'subcontractorId');
        const subcontractorName = pickValue('SubContractorName', 'SubcontractorName', 'subcontractorName');
        const tripIds = pickValue('TripIDs', 'tripIds');
        const values = {
            memberId,
            tripIds,
            subcontractorId,
            passNumber: pickValue('TicketNumber', 'passNumber'),
            memberNote: pickValue('CCERRTSMbrNote', 'memberNote'),
            busPassType: undefined,
            username: storedUserName || pickValue('username', 'ClientName'),
            transitAgency: pickValue('TransitAgency', 'transitAgency'),
        };

        form.setFieldsValue(values);
        setReadOnlyValues(values);
        setMemberDisplay(memberName && memberId ? `${memberName} - ${memberId}` : (memberName || memberId));
        setMemberNavigationId(memberNavigationIdValue);
        setTripLegsDisplay(pickValue('TripLegIDs', 'tripLegIds', 'TripLegs') || tripIds);
        setPreferredPassTagDisplay(pickValue('LatestTripTagNames', 'preferredPassTag'));
        setVendorDisplay(subcontractorName);
    }, [initialValues, form]);

    const showNotification = (
        type: 'success' | 'error',
        message: string,
        description?: string,
    ) => {
        notificationApi[type]({
            message,
            description,
        });
    };

    const handleViewReport = async () => {
        try {
            const values = (await form.validateFields()) as BusPassReportFormValues;
            setSubmitting(true);

            const response = await generateBusPass(values);
            showNotification(
                'success',
                'Bus pass request submitted',
                response?.message ?? 'Bus pass request submitted successfully.',
            );
            setTimeout(() => {
                onReportGenerated?.(response);
            }, 200)
        } catch (error: any) {
            if (error?.errorFields) {
                return;
            }
            const serverMessage =
                error?.messages?.[0]?.description ??
                error?.message ??
                'Unable to process the report request.';
            showNotification('error', 'Generate bus pass failed', serverMessage);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <>
            {notificationContextHolder}
            <Form
                layout="vertical"
                form={form}
                autoComplete="off"
            >
                <Form.Item name="memberId" hidden>
                    <Input />
                </Form.Item>
                <Form.Item name="tripIds" hidden>
                    <Input />
                </Form.Item>
                <Form.Item name="subcontractorId" hidden>
                    <Input />
                </Form.Item>
                <Form.Item name="username" hidden>
                    <Input />
                </Form.Item>
                <Form.Item name="transitAgency" hidden>
                    <Input />
                </Form.Item>
                <Row gutter={[24, 12]}>
                    <Col xs={24} md={12}>
                        <ReadOnlyField
                            label="Member"
                            value={memberNavigationId ? (
                                <a
                                    href={getMemberDetailUrl(memberNavigationId)}
                                    target="_blank"
                                    rel="noreferrer"
                                >
                                    {memberDisplay}
                                </a>
                            ) : memberDisplay}
                        />
                    </Col>
                    <Col xs={24} md={12}>
                        <ReadOnlyField label="Trip Legs" value={tripLegsDisplay} />
                    </Col>
                    <Col xs={24} md={12}>
                        <ReadOnlyField label="Preferred Pass Tag" value={preferredPassTagDisplay} />
                    </Col>
                    <Col xs={24} md={12}>
                        <ReadOnlyField label="Vendor" value={vendorDisplay} />
                    </Col>
                    <Col xs={24} md={12}>
                        <ReadOnlyField label="Username" value={readOnlyValues.username} />
                    </Col>
                    <Col xs={24} md={12}>
                        <ReadOnlyField label="Transit Agency" value={readOnlyValues.transitAgency} />
                    </Col>
                    <Col xs={24} md={12}>
                        <Form.Item
                            label="Pass Number"
                            name="passNumber"
                            rules={[{ required: true, message: 'Pass Number is required' }]}
                        >
                            <Input placeholder="Enter Pass Number" allowClear />
                        </Form.Item>
                    </Col>
                    <Col xs={24} md={12}>
                        <Form.Item
                            label="Bus Pass Type"
                            name="busPassType"
                            rules={[{ required: true, message: 'Bus Pass Type is required' }]}
                        >
                            <Select
                                placeholder="Select Type"
                                allowClear
                                showSearch
                                optionFilterProp="children"
                            >
                                {BUS_PASS_TYPE_OPTIONS.map((option) => (
                                    <Option key={option} value={option}>
                                        {option}
                                    </Option>
                                ))}
                            </Select>
                        </Form.Item>
                    </Col>
                    <Col xs={24} md={12}>
                        <Form.Item
                            label="Reservation Note"
                            name="memberNote"
                            rules={[{ required: true, message: 'Reservation Note is required' }]}
                        >
                            <TextArea placeholder="Enter the note" rows={4} allowClear />
                        </Form.Item>
                    </Col>
                    <Col
                        xs={24}
                        style={{
                            display: 'flex',
                            justifyContent: 'flex-end',
                        }}
                    >
                        <Button
                            type="primary"
                            htmlType="button"
                            onClick={handleViewReport}
                            loading={submitting}
                        >
                            Submit
                        </Button>
                    </Col>
                </Row>
            </Form>
        </>
    );
};

export default BusPassReportForm;
